import { FC, ReactNode } from 'react';
import { Box, styled, Typography } from '@mui/material';
import { ALERT_COLORS, AlertColorType } from '@utils/constants/color';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';

interface AlertBoxProps {
  type: AlertColorType;
  message: string | ReactNode;
  showIcon?: boolean;
}

interface StyledAlertBoxProps {
  colorType: AlertColorType;
}

const StyledAlertBox = styled(Box, {
  shouldForwardProp: prop => prop !== 'colorType',
})<StyledAlertBoxProps>(({ colorType }) => {
  const colors = ALERT_COLORS[colorType];

  return {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    borderRadius: '4px',
    backgroundColor: colors.background,
    border: `1px solid ${colors.border}`,
    '& .alert-icon': {
      color: colors.text,
      fontSize: '20px',
    },
    '& .alert-message': {
      color: colors.text,
      fontSize: '14px',
      fontWeight: 500,
    },
  };
});

const iconMap: Record<AlertColorType, FC<{ className?: string }>> = {
  success: props => <CheckCircleIcon {...props} />,
  error: props => <CancelIcon {...props} />,
  warning: props => <WarningIcon {...props} />,
  info: props => <InfoIcon {...props} />,
  infoLight: props => <InfoIcon {...props} />,
  restriction: props => <WarningIcon {...props} />,
};

const AlertBox: FC<AlertBoxProps> = ({ type, message, showIcon = true }) => {
  const IconComponent = iconMap[type];

  return (
    <StyledAlertBox colorType={type}>
      {showIcon && <IconComponent className='alert-icon' />}
      <Typography className='alert-message'>{message}</Typography>
    </StyledAlertBox>
  );
};

export default AlertBox;
